import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

const QUANTITY_DEBOUNCE_MS = 300;

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  /**
   * Debounce timers keyed by cart line key.
   *
   * This used to be a single shared timer for the whole component. Because a
   * debounce keeps one timeout and fires with only the LAST call's arguments,
   * tapping "+" on one line and then another within 300ms silently discarded the
   * first line's update: the shopper watched the number go up, then watched the
   * morph put it back, and nothing ever reached the server. Per-line timers keep
   * the intents independent.
   *
   * @type {Map<string, ReturnType<typeof setTimeout>>}
   */
  #quantityTimers = new Map();

  /**
   * Latest desired quantity per cart line key, waiting for a free slot.
   * @type {Map<string, number>}
   */
  #pendingQuantities = new Map();

  /**
   * Cart line keys waiting to be removed.
   * @type {Set<string>}
   */
  #pendingRemovals = new Set();

  /**
   * True while a cart mutation is in flight.
   *
   * This serialises requests so two rapid taps cannot race two conflicting
   * /cart/change.js calls — the job the `pointer-events: none` overlay used to
   * do, without making the cart untappable. Taps during flight are queued here
   * instead of falling through to the bare section <div> behind.
   */
  #inFlight = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    for (const timer of this.#quantityTimers.values()) clearTimeout(timer);
    this.#quantityTimers.clear();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
  }

  /**
   * The cart line rows, read straight from the DOM.
   *
   * `refs` is refreshed in a microtask after a morph, so anything resolving a
   * line number immediately after one reads the live DOM instead of a snapshot.
   *
   * @returns {HTMLElement[]} The rows, in cart order.
   */
  get #rows() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[ref="cartItemRows[]"]')]);
  }

  /**
   * Resolves a cart line key to its current 1-based line number.
   *
   * Line positions shift as rows are removed, so this is read at dispatch time
   * rather than captured when the shopper tapped.
   *
   * @param {string} key - The cart line key.
   * @returns {number | null} The line number, or null if the row is gone.
   */
  #lineNumberForKey(key) {
    const index = this.#rows.findIndex((row) => row.dataset.key === key);

    return index === -1 ? null : index + 1;
  }

  /**
   * Handles QuantitySelectorUpdateEvent, debounced per cart line.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantitySelectorUpdate = (event) => {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    const lineItemRow = this.refs.cartItemRows?.[line - 1];
    const key = lineItemRow?.dataset.key;

    if (!key) return;

    const timer = this.#quantityTimers.get(key);
    if (timer) clearTimeout(timer);
    this.#quantityTimers.delete(key);

    // Stepping below the minimum removes the line. That is a deliberate one-shot
    // action, so it skips the debounce.
    if (quantity === 0) {
      this.#pendingQuantities.delete(key);
      this.#requestRemoval(key);
      return;
    }

    this.#quantityTimers.set(
      key,
      setTimeout(() => {
        this.#quantityTimers.delete(key);
        this.#pendingQuantities.set(key, quantity);
        this.#drainPending();
      }, QUANTITY_DEBOUNCE_MS)
    );

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  };

  /**
   * Removes a line now, or queues it if a mutation is already in flight.
   * @param {string} key - The cart line key.
   */
  #requestRemoval(key) {
    if (this.#inFlight) {
      this.#pendingRemovals.add(key);
      return;
    }

    const line = this.#lineNumberForKey(key);
    if (line != null) this.onLineItemRemove(line);
  }

  /**
   * Dispatches the next queued mutation. Called after each request settles, so
   * queued taps land one at a time instead of racing.
   */
  #drainPending() {
    if (this.#inFlight) return;

    for (const key of this.#pendingRemovals) {
      this.#pendingRemovals.delete(key);
      this.#pendingQuantities.delete(key);

      const line = this.#lineNumberForKey(key);
      if (line == null) continue;

      this.onLineItemRemove(line);
      return;
    }

    for (const [key, quantity] of this.#pendingQuantities) {
      this.#pendingQuantities.delete(key);

      const line = this.#lineNumberForKey(key);
      if (line == null) continue;

      this.updateQuantity({ line, quantity, action: 'change' });
      return;
    }
  }

  /**
   * Re-applies queued quantities to their inputs after a morph, so a number the
   * shopper has already set does not visibly snap back to the server's older
   * value while its own request is still waiting in the queue.
   */
  #restorePendingInputs() {
    for (const [key, quantity] of this.#pendingQuantities) {
      const input = this.#rows.find((row) => row.dataset.key === key)?.querySelector('input[data-cart-line]');

      if (input instanceof HTMLInputElement) input.value = quantity.toString();
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows?.[line - 1];

    if (!cartItemRowToRemove) return;

    // A remove tapped while another mutation is running is queued, not dropped.
    // The button stays hit-testable throughout, so the tap is never swallowed.
    if (this.#inFlight) {
      const queuedKey = cartItemRowToRemove.dataset.key;
      if (queuedKey) this.#pendingRemovals.add(queuedKey);
      return;
    }

    const removedVariantId = cartItemRowToRemove.dataset.variantId;
    const parentVariantId = cartItemRowToRemove.dataset.parentVariantId;

    // Removing a bag → also remove linked embroidery items
    const embroideryRows = removedVariantId
      ? this.refs.cartItemRows.filter((row) => row.dataset.parentVariantId === removedVariantId)
      : [];

    // Removing an embroidery item → keep the parent bag, but strip the orphan
    // embroidery properties off it (Thread-Color, line_1, line_2, SKU, etc).
    // Otherwise 3PL reads the stale instructions on the bag and ships a blank pocket.
    const parentBagRow = parentVariantId
      ? this.refs.cartItemRows.find((row) => row.dataset.variantId === parentVariantId && !row.dataset.parentVariantId)
      : null;

    const rowsToRemove = [
      cartItemRowToRemove,
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      ...embroideryRows,
    ];

    // Only strip the bag's properties if no other embroidery line items remain linked to it
    const otherEmbroideryForBag = parentBagRow
      ? this.refs.cartItemRows.filter(
          (row) =>
            row.dataset.parentVariantId === parentBagRow.dataset.variantId && !rowsToRemove.includes(row)
        )
      : [];
    const shouldStripBagProperties =
      parentBagRow && parentBagRow.dataset.key && otherEmbroideryForBag.length === 0;

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });

    if (shouldStripBagProperties) {
      this.#removeEmbroideryAndCleanBag(rowsToRemove, parentBagRow.dataset.key);
    } else if (embroideryRows.length > 0) {
      // Batch remove all linked items via /cart/update.js
      const updates = {};
      rowsToRemove.forEach((row) => {
        if (row.dataset.key) updates[row.dataset.key] = 0;
      });
      this.#batchRemoveItems(updates);
    } else {
      this.updateQuantity({
        line,
        quantity: 0,
        action: 'clear',
      });
    }
  }

  /**
   * Removes multiple cart items at once by their keys.
   * @param {Record<string, 0>} updates - Map of cart item keys to quantity 0.
   */
  #batchRemoveItems(updates) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');

    this.#beginUpdate();

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          console.error('Batch remove error:', parsedResponseText.errors);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
        this.#restorePendingInputs();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#endUpdate();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Strips embroidery line-item properties off the parent bag and removes the embroidery line(s).
   * Why: when an embroidery upsell is removed alone, the bag was left with stale Thread-Color /
   * line_1 / SKU properties — 3PL reads those and ships a blank pocket since no embroidery line
   * is in the order. Cleaning the bag's properties prevents the orphan-instructions case.
   *
   * @param {HTMLElement[]} rowsToRemove - Embroidery rows being removed (and their nested lines).
   * @param {string} parentKey - Cart line key of the parent bag whose properties to clean.
   */
  #removeEmbroideryAndCleanBag(rowsToRemove, parentKey) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker('clear:user-action');
    const EMBROIDERY_PROPERTY_KEYS = new Set([
      'Thread-Color',
      'Color',
      'line_1',
      'line_2',
      'top_line',
      'bottom_line',
      'upsell_price',
      'Upsell Price',
      'SKU',
      'sku',
      '_parent_id',
    ]);

    this.#beginUpdate();
    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });
    const sectionsParam = Array.from(sectionsToUpdate).join(',');

    const embroideryUpdates = {};
    rowsToRemove.forEach((row) => {
      if (row.dataset.key && row.dataset.key !== parentKey) {
        embroideryUpdates[row.dataset.key] = 0;
      }
    });

    // Shopify line-item properties are IMMUTABLE — /cart/change.js with new properties
    // returns 200 but does not actually mutate them. To clean the bag we have to
    // remove the bag and re-add it with cleaned properties.
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        const parentItem = cart.items?.find((item) => item.key === parentKey);
        if (!parentItem) {
          // Bag not in cart anymore — just remove the embroidery line(s) with sections
          return fetch(
            `${Theme.routes.cart_update_url}`,
            fetchConfig('json', {
              body: JSON.stringify({
                updates: embroideryUpdates,
                sections: sectionsParam,
                sections_url: window.location.pathname,
              }),
            })
          );
        }

        const cleanedProperties = {};
        if (parentItem.properties) {
          for (const [key, value] of Object.entries(parentItem.properties)) {
            if (!EMBROIDERY_PROPERTY_KEYS.has(key)) cleanedProperties[key] = value;
          }
        }

        const sellingPlanId = parentItem.selling_plan_allocation?.selling_plan?.id;
        const variantId = parentItem.variant_id;
        const bagQuantity = parentItem.quantity;

        // 1) Remove embroidery line(s) + the polluted bag in one /cart/update.js call
        const removeUpdates = { ...embroideryUpdates, [parentKey]: 0 };

        return fetch(
          `${Theme.routes.cart_update_url}`,
          fetchConfig('json', {
            body: JSON.stringify({ updates: removeUpdates }),
          })
        ).then(() => {
          // 2) Re-add the bag with cleaned properties; ask for sections to render
          const addItem = {
            id: variantId,
            quantity: bagQuantity,
            properties: cleanedProperties,
          };
          if (sellingPlanId) addItem.selling_plan = sellingPlanId;

          return fetch(
            `${Theme.routes.cart_add_url}`,
            fetchConfig('json', {
              body: JSON.stringify({
                items: [addItem],
                sections: sectionsParam,
                sections_url: window.location.pathname,
              }),
            })
          );
        });
      })
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          console.error('Embroidery cleanup error:', parsedResponseText.errors);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
        this.#restorePendingInputs();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#endUpdate();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#beginUpdate();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
        this.#restorePendingInputs();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#endUpdate();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Marks a cart mutation as started: sets the in-flight lock and the busy
   * styling. The styling is visual and assistive only — every control stays
   * hit-testable, so a tap always lands on the control the shopper aimed at.
   */
  #beginUpdate() {
    this.#inFlight = true;
    this.classList.add('cart-items-busy');
    this.setAttribute('aria-busy', 'true');
  }

  /**
   * Clears the in-flight lock and the busy styling, then dispatches whatever the
   * shopper queued while the request was running.
   */
  #endUpdate() {
    this.#inFlight = false;
    this.classList.remove('cart-items-busy');
    this.removeAttribute('aria-busy');
    this.#drainPending();
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
